import React from 'react'

import { useId } from '@react-aria/utils'
import { useI18nContext } from '../_hooks/I18n'
import type { Props } from '../_utils/types'
import { StyledPill, StyledRemove, StyledRemoveButton } from './Pill.styles'
import type { PillColor, PillProps } from './Pill.types'

type SVGAttributesWithPillColor = Omit<
  React.SVGAttributes<SVGElement>,
  'color'
> & {
  color: PillColor
}

const Remove = React.forwardRef<
  SVGSVGElement,
  SVGAttributesWithPillColor & Props
>(function TokenRemove({ color = 'gray', ...props }, ref) {
  const i18n = useI18nContext()
  return (
    <StyledRemove ref={ref} role="img" size="sm" $color={color} {...props} />
  )
})

/**

 Pills are used to highlight important information in a list or form such as
 a status.

 @since 10.19.0

 @see [Storybook](https://stories.core.procore.com/?path=/story/core-react_demos-pill--demo)

 @see [Design Guidelines](https://design.procore.com/pill)

 */
export const Pill = React.forwardRef<HTMLSpanElement, PillProps>(function Pill(
  { color = 'gray', onClear, ...props },
  ref
) {
  const i18n = useI18nContext()
  const contentId = useId()
  return (
    <StyledPill $color={color} {...props} ref={ref}>
      {props.children && <span id={contentId}>{props.children}</span>}
      {onClear && (
        <StyledRemoveButton
          type="button"
          aria-describedby={contentId}
          aria-label={i18n.t('core.token.remove')}
          onClick={onClear}
          $color={color}
        >
          <Remove color={color} />
        </StyledRemoveButton>
      )}
    </StyledPill>
  )
})
